import React, { useState, useEffect, useCallback } from 'react';
import { Shield, TrendingUp, Lock, AlertCircle, Globe, Award, Users, Building, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BenefitsSection = () => {
  const [currentBenefit, setCurrentBenefit] = useState(0);
  const benefits = [
    {
      icon: Shield,
      title: 'Bulletproof Protection',
      description: 'Secure exclusive nationwide rights and stop competitors from stealing your brand identity - protect what you have built'
    },
    {
      icon: TrendingUp,
      title: 'Increase Business Value',
      description: 'Transform your brand into a valuable asset - registered trademarks can boost company valuation by up to 20%'
    },
    {
      icon: Lock,
      title: 'Unlock Revenue Streams',
      description: 'License your trademark, franchise your business, or sell your brand - maximize your earning potential with protection'
    },
    {
      icon: AlertCircle,
      title: 'Win Legal Battles Easily',
      description: 'Get automatic legal advantages in disputes - federal registration makes it simple to enforce your rights and stop infringers'
    },
    {
      icon: Globe,
      title: 'Dominate Your Market',
      description: 'Claim exclusive rights in all 50 states - expand without fear of brand conflicts or legal challenges'
    },
    {
      icon: Award,
      title: 'Beat the Competition',
      description: 'Create an unbeatable competitive advantage - legally prevent rivals from copying your success formula'
    },
    {
      icon: Users,
      title: 'Build Customer Loyalty',
      description: 'Earn instant credibility with the ® symbol - customers trust registered brands 3x more than unregistered ones'
    },
    {
      icon: Building,
      title: 'Scale with Confidence',
      description: 'Expand, partner, and grow fearlessly - protected brands attract investors and partners who demand security'
    }
  ];

  const nextBenefit = useCallback(() => setCurrentBenefit(p => (p + 1) % benefits.length), [benefits.length]);

  useEffect(() => {
    const timer = setInterval(nextBenefit, 4000);
    return () => clearInterval(timer);
  }, [nextBenefit]);

  return (
    <section className="relative py-20 overflow-hidden" aria-labelledby="benefits-heading">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1703355685722-2996b01483be" 
          alt="Background" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/95 via-[#1a1a3e]/90 to-black/95"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#FF035B] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 shadow-lg">
            Powerful Advantages
          </div>
          <h2 id="benefits-heading" className="text-3xl md:text-4xl font-bold text-white mb-6">
            Why Smart Businesses Choose Trademark Protection
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Stop leaving money on the table. Trademark registration delivers measurable ROI through legal protection, brand equity, and competitive advantages your rivals don't have
          </p>
        </div>

        {/* Benefits Grid - Horizontal scroll on mobile */}
        <div className="lg:grid lg:grid-cols-4 md:grid md:grid-cols-2 gap-8 hidden md:grid">
          {/* Desktop Grid */}
          {benefits.map((benefit, index) => {
            return (
              <div
                key={index}
                className="group relative bg-[#2F1A8E] hover:bg-[#FF035B] rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 hover:scale-105 border-2 border-white/20 backdrop-blur-sm"
              >
                {/* Icon - Outlined Style */}
                <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 border-2 border-white/30 shadow-xl">
                  <benefit.icon className="w-10 h-10 text-white stroke-[1.5]" />
                </div>
                
                {/* Content - Center Aligned */}
                <h3 className="text-lg font-bold text-white mb-4 text-center group-hover:scale-105 transition-transform duration-300">
                  {benefit.title}
                </h3>
                <p className="text-white/90 leading-relaxed text-center">
                  {benefit.description}
                </p>

                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-2xl"></div>
              </div>
            );
          })}
        </div>

        {/* Mobile Auto-Slider */}
        <div className="md:hidden">
          <div className="overflow-hidden">
            <div className="transition-transform duration-500 ease-in-out flex" style={{ transform: `translateX(-${currentBenefit * 100}%)` }}>
              {benefits.map((benefit, index) => (
                <div key={index} className="w-full flex-shrink-0 px-2">
                  <div className="group relative bg-[#2F1A8E] rounded-2xl p-6 shadow-2xl border-2 border-white/20">
                    <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 border-2 border-white/30">
                      <benefit.icon className="w-7 h-7 text-white stroke-[1.5]" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3 text-center">{benefit.title}</h3>
                    <p className="text-sm text-white/90 leading-relaxed text-center">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center items-center gap-4 mt-4">
            <button onClick={() => setCurrentBenefit(p => (p - 1 + benefits.length) % benefits.length)} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex gap-2">
              {benefits.map((_, i) => (
                <button key={i} onClick={() => setCurrentBenefit(i)} className={`h-2 rounded-full transition-all ${currentBenefit === i ? 'bg-[#FF035B] w-6' : 'bg-white/30 w-2'}`} />
              ))}
            </div>
            <button onClick={nextBenefit} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;